import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Flame, Skull } from "lucide-react";
import { ASSETS } from "@/lib/assets";
import StatusPanel from "@/components/StatusPanel";
import ChampionSpotlight from "@/components/ChampionSpotlight";
import MemeOfMoment from "@/components/MemeOfMoment";
import RoadmapPreview from "@/components/RoadmapPreview";
import TelegramBanner from "@/components/TelegramBanner";
import LiveNowStrip from "@/components/LiveNowStrip";
import ChainCard from "@/components/ChainCard";
import { useSettings } from "@/hooks/useSettings";

const MARQUEE = [
  "BOUGHT HIGH. SOLD LOW. ROBBED.",
  "NO SUITS. NO BANKS. JUST MEMES.",
  "IN CRYPTO, EVERYONE GETS ROBBED.",
  "DON'T GET ROBBED.",
  "WELCOME TO THE ROBBED.",
];

export default function Home() {
  const { data } = useSettings();
  const chains = data?.chains || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden halftone border-b-3 border-black">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <span className="tag animate-wobble inline-block">🟢 PRE-LAUNCH · TOKEN NOT LIVE</span>
            <h1 className="headline text-cream mt-6 text-5xl sm:text-6xl lg:text-7xl">
              THEY GOT RICH.<br />
              <span className="text-neon">WE GOT ROBBED.</span>
            </h1>
            <p className="font-heading font-bold uppercase text-dim mt-5 text-base sm:text-lg tracking-wide">
              The People's Meme Coin. The internet's most financially questionable support group.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/wall" className="btn-neon" data-testid="hero-enter-btn">
                Enter The Robbed <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/memes" className="btn-ghost" data-testid="hero-memes-btn">Make A Meme</Link>
              <Link to="/chains" className="btn-ghost" data-testid="hero-chain-btn">Choose Your Chain</Link>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative flex justify-center"
          >
            <div className="absolute w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-neon/20 blur-3xl" />
            <img
              src={ASSETS.pointing}
              alt="ROBBED mascot"
              className="relative w-64 sm:w-80 lg:w-[26rem] drop-shadow-[8px_8px_0_rgba(0,0,0,0.6)] animate-floaty"
              data-testid="hero-mascot"
            />
          </motion.div>
        </div>

        {/* marquee */}
        <div className="bg-neon border-t-3 border-black overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee py-2">
            {[0, 1].map((k) => (
              <span key={k} className="flex">
                {MARQUEE.map((m, i) => (
                  <span key={i} className="font-display font-900 text-black uppercase text-sm mx-6">✦ {m}</span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STATUS + PILLARS */}
      <LiveNowStrip />
      <MemeOfMoment />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4" data-testid="home-community"><TelegramBanner /></section>
      <RoadmapPreview />
      <ChampionSpotlight />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-8 items-start">
        <StatusPanel />
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: Users, t: "Community First", d: "It's a movement, not a market. The Robbed run this." },
            { icon: Flame, t: "Meme Powered", d: "Turn your worst trades into the internet's best memes." },
            { icon: Skull, t: "Zero Fake Data", d: "No fabricated price, holders or liquidity. Ever." },
            { icon: ArrowRight, t: "Multichain Ready", d: "Built for Robinhood Chain + Solana from day one." },
          ].map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="comic-card p-5"
            >
              <p.icon className="w-7 h-7 text-neon" />
              <h4 className="font-display font-900 text-cream mt-3 text-base uppercase">{p.t}</h4>
              <p className="font-mono text-xs text-dim mt-2 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CHAINS PREVIEW */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <h2 className="headline text-cream text-3xl sm:text-4xl">$ROBBED IS <span className="text-neon">EVERYWHERE</span></h2>
          <Link to="/chains" className="btn-ghost !py-2 !px-4 !text-xs">See All Chains <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {chains.map((c) => <ChainCard key={c.chainName} chain={c} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="comic-card p-8 sm:p-12 relative overflow-hidden text-center">
          <div className="halftone absolute inset-0 opacity-30" />
          <div className="relative">
            <h2 className="headline text-cream text-3xl sm:text-5xl">DON'T GET ROBBED ALONE.</h2>
            <p className="font-heading font-bold uppercase text-dim mt-3">Join the crew. Post your damage. Vote the memes.</p>
            <div className="flex justify-center gap-4 mt-8 flex-wrap">
              <Link to="/login" className="btn-neon" data-testid="cta-join-btn">Join The Community</Link>
              <Link to="/wall" className="btn-ghost">What Robbed You?</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
